import React, { useState, useEffect } from 'react'
import { useAuthStore } from '../store/useAuthStore'

const InvoiceManagementPage = () => {
  const { profile } = useAuthStore()
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState(null)

  // Mock invoices data
  useEffect(() => {
    const mockInvoices = [
      {
        id: 1,
        invoiceNumber: 'INV-2024-001',
        clientName: 'John Doe',
        projectName: 'E-commerce Website',
        amount: 150000,
        currency: 'BDT',
        status: 'paid',
        dueDate: '2024-01-15',
        createdDate: '2024-01-01',
        paidDate: '2024-01-10',
        items: [
          { description: 'Website Design', quantity: 1, rate: 50000, amount: 50000 },
          { description: 'Frontend Development', quantity: 1, rate: 60000, amount: 60000 },
          { description: 'Backend Development', quantity: 1, rate: 40000, amount: 40000 }
        ]
      },
      {
        id: 2,
        invoiceNumber: 'INV-2024-002',
        clientName: 'Jane Smith',
        projectName: 'Mobile App Development',
        amount: 200000,
        currency: 'BDT',
        status: 'sent',
        dueDate: '2024-02-15',
        createdDate: '2024-01-20',
        paidDate: null,
        items: [
          { description: 'iOS App Development', quantity: 1, rate: 100000, amount: 100000 },
          { description: 'Android App Development', quantity: 1, rate: 100000, amount: 100000 }
        ]
      },
      {
        id: 3,
        invoiceNumber: 'INV-2024-003',
        clientName: 'Mike Johnson',
        projectName: 'Digital Marketing',
        amount: 75000,
        currency: 'BDT',
        status: 'overdue',
        dueDate: '2024-01-30',
        createdDate: '2024-01-15',
        paidDate: null,
        items: [
          { description: 'SEO Optimization', quantity: 1, rate: 30000, amount: 30000 },
          { description: 'Social Media Management', quantity: 3, rate: 15000, amount: 45000 }
        ]
      }
    ]
    
    setTimeout(() => {
      setInvoices(mockInvoices)
      setLoading(false)
    }, 1000)
  }, [])

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800'
      case 'sent': return 'bg-blue-100 text-blue-800'
      case 'overdue': return 'bg-red-100 text-red-800'
      case 'draft': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const generateInvoice = () => {
    // In real app, this would generate PDF
    alert('Invoice PDF generated successfully!')
  }

  const sendInvoice = (invoiceId) => {
    // In real app, this would send email
    setInvoices(prev => prev.map(inv => 
      inv.id === invoiceId ? { ...inv, status: 'sent' } : inv
    ))
    alert('Invoice sent to client!')
  }

  const markAsPaid = (invoiceId) => {
    setInvoices(prev => prev.map(inv => 
      inv.id === invoiceId ? { 
        ...inv, 
        status: 'paid', 
        paidDate: new Date().toISOString().split('T')[0] 
      } : inv
    ))
    alert('Invoice marked as paid!')
  }

  const getTotalRevenue = () => {
    return invoices
      .filter(inv => inv.status === 'paid')
      .reduce((total, inv) => total + inv.amount, 0)
  }

  const getPendingAmount = () => {
    return invoices
      .filter(inv => inv.status !== 'paid')
      .reduce((total, inv) => total + inv.amount, 0)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="page-container py-8">
          <div className="animate-pulse">
            <div className="bg-gray-200 h-8 w-64 rounded mb-8"></div>
            <div className="bg-gray-200 h-64 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="page-container py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Invoice Management</h1>
              <p className="text-gray-600 mt-2">Manage invoices, payments, and billing</p>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => setShowCreateForm(true)}
                className="btn-primary"
              >
                Create Invoice
              </button>
              <button className="btn-secondary">Export Invoices</button>
            </div>
          </div>
        </div>
      </div>

      <div className="page-container py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-2xl font-semibold text-gray-900">৳{getTotalRevenue().toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending Amount</p>
                <p className="text-2xl font-semibold text-gray-900">৳{getPendingAmount().toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Invoices</p>
                <p className="text-2xl font-semibold text-gray-900">{invoices.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Invoices Table */}
        <div className="card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Invoice #</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Client</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Project</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Amount</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Due Date</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm font-medium">{invoice.invoiceNumber}</td>
                    <td className="py-3 px-4 text-sm">{invoice.clientName}</td>
                    <td className="py-3 px-4 text-sm">{invoice.projectName}</td>
                    <td className="py-3 px-4 text-sm font-semibold">৳{invoice.amount.toLocaleString()}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(invoice.status)}`}>
                        {invoice.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {new Date(invoice.dueDate).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setSelectedInvoice(invoice)}
                          className="text-primary-600 hover:text-primary-700 text-sm"
                        >
                          View
                        </button>
                        <button
                          onClick={() => generateInvoice()}
                          className="text-gray-600 hover:text-gray-700 text-sm"
                        >
                          PDF
                        </button>
                        {invoice.status === 'draft' && (
                          <button
                            onClick={() => sendInvoice(invoice.id)}
                            className="text-blue-600 hover:text-blue-700 text-sm"
                          >
                            Send
                          </button>
                        )}
                        {invoice.status === 'sent' && (
                          <button
                            onClick={() => markAsPaid(invoice.id)}
                            className="text-green-600 hover:text-green-700 text-sm"
                          >
                            Mark Paid
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Invoice Detail Modal */}
        {selectedInvoice && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Invoice Details</h2>
                <button
                  onClick={() => setSelectedInvoice(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Invoice Number</label>
                    <p className="text-gray-900">{selectedInvoice.invoiceNumber}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Status</label>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(selectedInvoice.status)}`}>
                      {selectedInvoice.status.toUpperCase()}
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Client</label>
                    <p className="text-gray-900">{selectedInvoice.clientName}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Project</label>
                    <p className="text-gray-900">{selectedInvoice.projectName}</p>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-600">Items</label>
                  <div className="mt-2 space-y-2">
                    {selectedInvoice.items.map((item, index) => (
                      <div key={index} className="flex justify-between py-2 border-b border-gray-100">
                        <div>
                          <p className="font-medium">{item.description}</p>
                          <p className="text-sm text-gray-600">Qty: {item.quantity} × ৳{item.rate.toLocaleString()}</p>
                        </div>
                        <p className="font-semibold">৳{item.amount.toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total Amount</span>
                    <span>৳{selectedInvoice.amount.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default InvoiceManagementPage
